import {Helmet} from '@dr.pogodin/react-helmet';
import {Dialog, showNotification} from 'platform/components';

import {useState} from 'react';

import {head, isNil, not} from 'ramda';

import {usePostFastTrackIssueMutation} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  DmsDataGrid,
  WAREHOUSE_GRID_CODES,
  handleApiError,
  logError,
  usePermissions,
} from '@dms/shared';

import {composePath, Nullish, useNavigate} from 'shared/web';

import type {RowData} from 'features/eag-grid';

type CastedRowData = RowData & {
  orderType: {
    value: {
      key: 'direct-sale' | 'service-case-order';
    };
  };
};

const PREDEFINED_COLUMNS = ['orderType'] as const;

export const FastTrackIssuesCreate = () => {
  const navigate = useNavigate();

  const [canCreateFastTrackIssue] = usePermissions({
    permissionKeys: ['fastTrackIssueCreate'],
  });

  const [postFastTrackIssue, {isLoading: isAssigningOrder}] = usePostFastTrackIssueMutation();

  const [selectedOrder, setSelectedOrder] = useState<CastedRowData | Nullish>(null);

  const handleDiscard = () => {
    navigate(warehouseRoutes.fastTrackIssues);
  };

  const handleRowSelection = (rows: RowData[]) => {
    const selectedRow = head(rows);
    const castedRow = selectedRow as CastedRowData;
    setSelectedOrder(castedRow);
  };

  const handleAssignOrder = () => {
    if (isNil(selectedOrder?.id)) {
      return logError('originEntityHeaderId is not defined');
    }

    if (isNil(selectedOrder?.orderType?.value?.key)) {
      return logError('originEntityHeaderType is not defined');
    }

    postFastTrackIssue({
      originEntityHeaderId: selectedOrder.id,
      originEntityHeaderType: selectedOrder.orderType.value.key,
    })
      .unwrap()
      .then((result) => {
        showNotification.success(i18n.t('general.notifications.successfullyCreated'));

        navigate(
          composePath(warehouseRoutes.fastTrackIssuesDetailOverview, {
            params: {id: result.fastTrackId},
          })
        );
      })
      .catch(handleApiError);
  };

  return (
    <>
      <Helmet title={i18n.t('page.warehouse.actions.newFastTrackIssue')} />
      <Dialog
        isOpen
        title={i18n.t('entity.warehouse.labels.assignOrder')}
        size="large"
        onClose={handleDiscard}
        buttons={[
          {
            isLoading: isAssigningOrder,
            type: 'button',
            variant: 'secondary',
            title: i18n.t('general.labels.discard'),
            onClick: handleDiscard,
          },
          {
            isLoading: isAssigningOrder,
            isDisabled: not(canCreateFastTrackIssue) || isNil(selectedOrder),
            type: 'button',
            title: i18n.t('general.labels.assign'),
            onClick: handleAssignOrder,
            'data-testid': testIds.warehouse.fastTrackIssuesCreate('assign'),
          },
        ]}
        data-testid={testIds.warehouse.fastTrackIssuesCreate('assignOrder')}
      >
        <DmsDataGrid
          gridCode={WAREHOUSE_GRID_CODES.fastTrackIssueAssignOrder}
          onRowSelectionChange={handleRowSelection}
          predefinedColumns={PREDEFINED_COLUMNS}
          autoHeight
          data-testid={WAREHOUSE_GRID_CODES.fastTrackIssueAssignOrder}
        />
      </Dialog>
    </>
  );
};
